/**
 * Draft Store — persistent workout and feed draft storage.
 *
 * Drafts survive app restarts via AsyncStorage.
 * In-memory cache avoids async reads on hot paths.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Exercise } from '@/types/database';
import { createLogger } from '@/lib/logger';

const log = createLogger('DRAFT_STORE');

const STORAGE_KEY_WORKOUT = 'duel:workout_drafts';
const STORAGE_KEY_FEED = 'duel:feed_drafts';

export interface DraftExerciseEntry {
  id: string;
  exercise: Exercise | null;
  weight: string;
  reps: string;
  sets: string;
  isPR: boolean;
  videoUri: string | null;
  notes: string;
}

export type WorkoutDraft = {
  exerciseEntries: DraftExerciseEntry[];
  workoutDate: string;
  sessionId?: string;
};

export type FeedDraft = {
  content: string;
  mediaUri: string | null;
  mediaType: 'video' | 'image' | null;
};

// In-memory caches (populated from storage on init)
const workoutDrafts = new Map<string, WorkoutDraft>();
const feedDrafts = new Map<string, FeedDraft>();
let initialized = false;

async function persistWorkoutDrafts(): Promise<void> {
  try {
    const obj = Object.fromEntries(workoutDrafts);
    await AsyncStorage.setItem(STORAGE_KEY_WORKOUT, JSON.stringify(obj));
  } catch (err: unknown) {
    log.error('Failed to persist workout drafts', err instanceof Error ? err.message : err);
  }
}

async function persistFeedDrafts(): Promise<void> {
  try {
    const obj = Object.fromEntries(feedDrafts);
    await AsyncStorage.setItem(STORAGE_KEY_FEED, JSON.stringify(obj));
  } catch (err: unknown) {
    log.error('Failed to persist feed drafts', err instanceof Error ? err.message : err);
  }
}

export const draftStore = {
  /**
   * Load drafts from AsyncStorage into memory. Call once on app start.
   * Safe to call multiple times (idempotent).
   */
  async init(): Promise<void> {
    if (initialized) return;
    try {
      const [wRaw, fRaw] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEY_WORKOUT),
        AsyncStorage.getItem(STORAGE_KEY_FEED),
      ]);

      if (wRaw) {
        const parsed = JSON.parse(wRaw) as Record<string, WorkoutDraft>;
        for (const [k, v] of Object.entries(parsed)) {
          workoutDrafts.set(k, v);
        }
      }
      if (fRaw) {
        const parsed = JSON.parse(fRaw) as Record<string, FeedDraft>;
        for (const [k, v] of Object.entries(parsed)) {
          feedDrafts.set(k, v);
        }
      }
      initialized = true;
      log.info(`Loaded ${workoutDrafts.size} workout draft(s), ${feedDrafts.size} feed draft(s)`);
    } catch (err: unknown) {
      log.error('Failed to load drafts from storage', err instanceof Error ? err.message : err);
      initialized = true; // Don't block on failure
    }
  },

  setWorkoutDraft(contextId: string, draft: WorkoutDraft) {
    workoutDrafts.set(contextId, draft);
    void persistWorkoutDrafts();
  },

  getWorkoutDraft(contextId: string): WorkoutDraft | undefined {
    return workoutDrafts.get(contextId);
  },

  clearWorkoutDraft(contextId: string) {
    workoutDrafts.delete(contextId);
    void persistWorkoutDrafts();
  },

  setFeedDraft(contextId: string, draft: FeedDraft) {
    feedDrafts.set(contextId, draft);
    void persistFeedDrafts();
  },

  getFeedDraft(contextId: string): FeedDraft | undefined {
    return feedDrafts.get(contextId);
  },

  clearFeedDraft(contextId: string) {
    feedDrafts.delete(contextId);
    void persistFeedDrafts();
  },

  /** Number of saved workout drafts */
  get workoutDraftCount(): number {
    return workoutDrafts.size;
  },

  /** Clear all drafts (for sign-out) */
  async clearAll(): Promise<void> {
    workoutDrafts.clear();
    feedDrafts.clear();
    await Promise.all([
      AsyncStorage.removeItem(STORAGE_KEY_WORKOUT),
      AsyncStorage.removeItem(STORAGE_KEY_FEED),
    ]);
  },
};
